import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { pendingJobStates } from '~pages/Dispatch/constants/jobStates';
import useDriverSchedulerFetch from '~pages/Dispatch/hooks/useDriverSchedulerFetch';
import { useStores } from '~store';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const DriversViewEffects = () => {
  const {
    fetchUnassignedJobs,
    fetchDrivers,
    fetchVendors,
    fetchAssignedJobs,
    subscribeToJobsRTU,
  } = useDriverSchedulerFetch();
  const { userStore, driverSchedulerStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [unassignedJobsInput] = useState('');

  const applyUnassignedJobFilter = debounce(() => {
    driverSchedulerStore.setUnassignedJobsFilters({ search: unassignedJobsInput }, true);
  }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

  useEffect(() => {
    applyUnassignedJobFilter();

    return () => {
      applyUnassignedJobFilter.cancel();
    };
  }, [unassignedJobsInput]);

  useEffect(() => {
    let jobsRTUSubscription: ReturnType<typeof subscribeToJobsRTU> | null = null;

    if (companyId) {
      jobsRTUSubscription = subscribeToJobsRTU(companyId);
    }

    return () => {
      jobsRTUSubscription?.unsubscribe?.();
    };
  }, [companyId]);

  useEffect(() => {
    const debouncedFetchUnassignedJobs = debounce(() => {
      if (!driverSchedulerStore.dateFilters.startDate) return;
      // The Orders sub-tab eager-loads all unassigned jobs itself; defer to it so
      // the two loaders never clear/fetch the same list concurrently.
      if (driverSchedulerStore.isOrdersViewActive) return;
      driverSchedulerStore.clearUnassignedJobs();
      void Promise.resolve(
        fetchUnassignedJobs({
          states: pendingJobStates,
          companyId,
        }),
      ).catch(() => {
        // Error is handled by the hook.
      });
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

    debouncedFetchUnassignedJobs();

    // Cancel the debounce if the component unmounts or dependencies change
    return () => {
      debouncedFetchUnassignedJobs.cancel();
    };
  }, [
    companyId,
    driverSchedulerStore.isOrdersViewActive,
    driverSchedulerStore.unassignedJobsFilters,
    driverSchedulerStore.dateFilters.startDate,
  ]);

  useEffect(() => {
    if (!companyId) return;
    driverSchedulerStore.clearAssignees();

    if (driverSchedulerStore.assigneesFilters.assigneeType === 'vendor') {
      void Promise.resolve(fetchVendors({ companyId })).catch(() => {
        // Error is handled by the hook.
      });
    } else {
      void Promise.resolve(fetchDrivers({ companyId })).catch(() => {
        // Error is handled by the hook.
      });
    }
  }, [
    companyId,
    driverSchedulerStore.assigneesFilters.assigneeType,
    driverSchedulerStore.assigneesFilters.search,
    driverSchedulerStore.dateFilters.startDate,
  ]);

  const currentAssignedJobsFilters = JSON.stringify(
    driverSchedulerStore.assignedJobsFilters,
  );
  const previousAssignedJobsFilters = usePrevious(currentAssignedJobsFilters);
  const assignedJobFiltersDidChange =
    previousAssignedJobsFilters !== currentAssignedJobsFilters;

  const currentStartDateFilter = driverSchedulerStore.dateFilters.startDate;
  const previousStartDateFilter = usePrevious(driverSchedulerStore.dateFilters.startDate);
  const startDateFilterDidChange = previousStartDateFilter !== currentStartDateFilter;

  useEffect(() => {
    const debouncedFetchAssignedJobs = debounce(() => {
      if (!driverSchedulerStore.dateFilters.startDate) {
        return;
      }

      void Promise.resolve(
        fetchAssignedJobs({
          assigneeIds: driverSchedulerStore.assignees.map((assignee) => assignee.id),
          companyId,
        }),
      ).catch(() => {
        // Error is handled by the hook.
      });
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

    debouncedFetchAssignedJobs();

    return () => {
      debouncedFetchAssignedJobs.cancel();
    };
  }, [
    companyId,
    assignedJobFiltersDidChange,
    driverSchedulerStore.assignees, // Don't clear data on driver change, just refetch
    startDateFilterDidChange,
    driverSchedulerStore.dateFilters.startDate,
    driverSchedulerStore.assignedJobsFilters,
  ]);

  return null;
};

export default observer(DriversViewEffects);
